import React, { useMemo } from "react";
import { Tag, Typography } from "antd";

import { ago } from "@components/Date";
import { type OrganizationPublicKey } from "@api/types";

import TableRowActions from "../TableRowActions";

const LABEL_NAME = "Name";
const LABEL_ID = "Key ID (kid)";
const LABEL_ISSUER = "Issuer (iss)";
const LABEL_STATUS = "Status";
const LABEL_LAST_USED_AT = "Last Used";
const LABEL_EMPTY = "-";

const STATUS_ACTIVE = "ACTIVE";

type Props = {
  onSelected: (selectedPublicKey: OrganizationPublicKey) => void;
  onRevoked: (revokedPublicKey: OrganizationPublicKey) => void;
};

// NOTE: the config keeps its identity while the handlers keep theirs. The
//       table sorts and indexes the items again for each new config, therefore
//       an unstable config makes the table load the items on each render.
const useColumnsConfig = ({ onSelected, onRevoked }: Props) => {
  const columnsConfig = useMemo(() => {
    const nameColumnProps = {
      key: "name",
      title: LABEL_NAME,
      render: (name: string, publicKey: OrganizationPublicKey) => (
        <Typography.Link strong onClick={() => onSelected(publicKey)}>
          {name}
        </Typography.Link>
      ),
      isSearchable: true,
    };

    const idColumnProps = {
      key: "id",
      title: LABEL_ID,
    };

    const issuerColumnProps = {
      key: "issuer",
      title: LABEL_ISSUER,
    };

    const lastUsedAtColumnProps = {
      key: "lastUsedAt",
      title: LABEL_LAST_USED_AT,
      render: (value?: string) => {
        const hasValue = !!value;

        if (!hasValue) {
          return LABEL_EMPTY;
        }

        return ago(value, true);
      },
    };

    const statusColumnProps = {
      key: "status",
      title: LABEL_STATUS,
      render: (status: string) => {
        const isRevoked = status !== STATUS_ACTIVE;

        if (isRevoked) {
          return <Tag color="red">{status}</Tag>;
        }

        return <Tag color="green">{status}</Tag>;
      },
    };

    const actionsColumnProps = {
      key: "actions",
      title: false,
      width: 48,
      render: (_: unknown, publicKey: OrganizationPublicKey) => (
        <TableRowActions
          publicKey={publicKey}
          onRevoked={onRevoked}
        />
      ),
    };

    return [
      { ...nameColumnProps },
      { ...idColumnProps },
      { ...issuerColumnProps },
      { ...lastUsedAtColumnProps },
      { ...statusColumnProps },
      { ...actionsColumnProps },
    ];
  }, [
    onRevoked,
    onSelected,
  ]);

  return columnsConfig;
};

export default useColumnsConfig;
